import { createTheme, alpha } from '@mui/material/styles';

const crimson = {
  light: '#E53333',
  main: '#C0202A',
  dark: '#8B0000',
  contrastText: '#F2EDE4',
};

const silver = '#C0C0C0';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: crimson,
    secondary: {
      main: silver,
      contrastText: '#0A0A0A',
    },
    background: {
      default: '#080808',
      paper: '#0E0E0E',
    },
    text: {
      primary: '#F2EDE4',
      secondary: '#9A9A9A',
    },
    divider: alpha(crimson.main, 0.25),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 400,
      letterSpacing: '0.02em',
    },
    h4: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 400,
      letterSpacing: '0.02em',
    },
    h5: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 500,
    },
    h6: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },
    subtitle1: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.1em',
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 300,
      lineHeight: 1.85,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 300,
      lineHeight: 1.7,
    },
    button: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.15em',
    },
    overline: {
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.2em',
      fontSize: '0.7rem',
    },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiSvgIcon: {
      defaultProps: {
        width: '1em',
        height: '1em',
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '14px 40px',
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${crimson.main} 0%, ${crimson.dark} 100%)`,
          color: '#F2EDE4',
          '&:hover': {
            background: `linear-gradient(135deg, ${crimson.light} 0%, ${crimson.main} 100%)`,
            transform: 'translateY(-1px)',
            boxShadow: `0 8px 30px ${alpha(crimson.main, 0.45)}`,
          },
        },
        outlinedPrimary: {
          borderColor: crimson.main,
          color: crimson.main,
          '&:hover': {
            borderColor: crimson.light,
            color: crimson.light,
            backgroundColor: alpha(crimson.main, 0.08),
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '& fieldset': {
              borderColor: alpha(crimson.main, 0.3),
            },
            '&:hover fieldset': {
              borderColor: alpha(crimson.main, 0.6),
            },
            '&.Mui-focused fieldset': {
              borderColor: crimson.main,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: crimson.main,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          background: '#111111',
          border: `1px solid ${alpha(crimson.main, 0.15)}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha(crimson.main, 0.25),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent !important',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent !important',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'transparent !important',
          },
          '&:focus-visible': {
            backgroundColor: 'transparent !important',
          },
        },
      },
    },
  },
});

export default theme;
